import posthog from "posthog-js";
import { name, version } from "./package.json";
import {
  observeAcquisitionUrl,
  readClientAcquisition,
} from "./src/lib/acquisition/client";
import { acquisitionProperties } from "./src/lib/acquisition/shared";
import { isEventPreviewContext } from "./src/lib/events/preview/path";
import { getLoadErrorRecoveryKind } from "./src/lib/load-error-recovery";
import { scheduleLoadErrorRecovery } from "./src/lib/load-error-recovery.client";
import {
  getPostHogExceptionError,
  shouldDropPostHogClientEvent,
} from "./src/lib/telemetry/client-error-policy";
import {
  buildTelemetryContext,
  deploymentEnvironment,
  isClientTelemetryDeliveryEnabled,
} from "./src/lib/telemetry/environment";
import { resolvePostHogIngestOrigin } from "./src/lib/telemetry/ingest-host";
import {
  buildRuntimeContext,
  isNativeCapacitorRuntime,
} from "./src/lib/telemetry/runtime";

const isNativeWebView = isNativeCapacitorRuntime();
const telemetryContext = buildTelemetryContext(version);

/**
 * Session replay pulls in the rrweb recorder. In the Capacitor WebView the
 * extra recorder script has historically obscured native startup errors and
 * triggered hook-order crashes, so native stays opt-in behind an env flag
 * rather than shipping a launch-day risk. Web gets it on by default.
 */
const nativeReplayEnabled =
  process.env.NEXT_PUBLIC_HIIVE_NATIVE_SESSION_REPLAY === "true";

/**
 * Session replay is OFF.
 *
 * It recorded every production session and was never actually used. That is
 * not a free default on a phone: the rrweb recorder uploads continuously, and
 * because Next 16 keeps every visited route mounted in the DOM (React
 * `<Activity>`), the surface it observes grows for as long as the session
 * lasts. One stuck session measured 142 MB across 978 requests, with replay
 * uploads (`/s/`) taking 8-18s and timing out — on the same connection the app
 * needs for its RSC navigations.
 *
 * Set NEXT_PUBLIC_HIIVE_SESSION_REPLAY=true to turn it back on for web; native
 * keeps its own flag because the recorder has historically masked native
 * startup errors in the Capacitor WebView.
 */
const webReplayEnabled =
  process.env.NEXT_PUBLIC_HIIVE_SESSION_REPLAY === "true";

const sessionReplayEnabled = isNativeWebView
  ? nativeReplayEnabled
  : webReplayEnabled;

/**
 * Autocapture — ON by default, and switchable from the environment so it can
 * be ruled out without a code change.
 *
 * It is the one part of posthog-js that does per-interaction work on the main
 * thread: global listeners for click / change / submit, and on each of those a
 * walk up the DOM to build a selector for the element. That cost is a function
 * of how large the document is.
 *
 * Which is the problem worth testing. Next 16 does not unmount the route you
 * navigate away from — it hides it in a React `<Activity>` — so the document
 * grows for as long as the session lasts. This is the same argument already
 * recorded above for session replay, which was measured at 142 MB across 978
 * requests in one stuck session, and it applies here for the same reason.
 *
 * Set NEXT_PUBLIC_HIIVE_AUTOCAPTURE=false to turn it off. Explicit
 * `posthog.capture()` calls are unaffected, which matters: the navigation
 * watchdog reports through one, so this test does not blind the instrument
 * measuring the thing being tested. Disabling PostHog outright would.
 */
const autocaptureEnabled =
  process.env.NEXT_PUBLIC_HIIVE_AUTOCAPTURE !== "false";

const posthogProjectKey = process.env.NEXT_PUBLIC_POSTHOG_KEY;

if (
  typeof window !== "undefined" &&
  isClientTelemetryDeliveryEnabled() &&
  posthogProjectKey
) {
  posthog.init(posthogProjectKey, {
    // `/ingest` (the default) is a same-origin path, which means telemetry
    // shares one HTTP/2 connection with every RSC navigation on the page.
    // Point NEXT_PUBLIC_POSTHOG_INGEST_ORIGIN at a dedicated host to give it
    // its own connection — see src/lib/telemetry/ingest-host.ts.
    api_host: resolvePostHogIngestOrigin(),
    ui_host: "https://us.posthog.com",
    defaults: "2026-01-30",
    // PostHog's exception recorder loads an additional browser recorder script.
    // In Capacitor WebViews it can obscure native startup errors and has caused
    // hook-order crashes during dev-agent native launches. Native therefore
    // reports exceptions through the manual global handlers installed by
    // components/providers/client-telemetry-provider.tsx instead — the events
    // are identical, only the collection path differs.
    capture_exceptions: !isNativeWebView,
    // debug: process.env.NODE_ENV === "development",
    person_profiles: "identified_only",
    // No feature flags are defined or read anywhere in this app — nothing calls
    // `isFeatureEnabled`, `getFeatureFlag` or a flag hook, and every captured
    // exception carries `$active_feature_flags: []`. posthog-js still fetches
    // them on init and again on every `identify`, which is where the repeated
    // `/flags/?v=2` requests in the network tab come from: a round trip per
    // page, on the same connection the app needs for its RSC navigations, to
    // learn that there are no flags.
    //
    // Turn this back off the day a flag is actually used — reading one with
    // this set returns undefined rather than failing loudly.
    advanced_disable_feature_flags: true,
    // Native navigation never triggers a document load, so posthog-js sees no
    // pageviews at all in the WebView. RouteAnalyticsReporter captures them
    // manually; enabling both would double-count on web.
    capture_pageview: !isNativeWebView,
    capture_pageleave: !isNativeWebView,
    autocapture: autocaptureEnabled,
    // Web click/scroll heatmaps are independent of the session replay recorder.
    enable_heatmaps:
      !isNativeWebView && process.env.NEXT_PUBLIC_HIIVE_HEATMAPS !== "false",
    before_send: (event) => {
      if (event)
        event.properties = {
          ...acquisitionProperties(readClientAcquisition()),
          ...event.properties,
          // Partners walking an event preview link: their pageviews, booking
          // funnel and clicks are real behaviour on a fake event, so every
          // event says so. Filter `is_preview != true` in any funnel.
          ...(isEventPreviewContext() ? { is_preview: true } : {}),
        };
      const error = getPostHogExceptionError(event);
      const recoveryKind = error ? getLoadErrorRecoveryKind(error) : null;
      if (error && recoveryKind) {
        scheduleLoadErrorRecovery(error, recoveryKind, () => {
          (posthog as typeof posthog & { flush?: () => void }).flush?.();
        });
      }

      return shouldDropPostHogClientEvent(event, isNativeWebView)
        ? null
        : event;
    },
    disable_session_recording: !sessionReplayEnabled,
    session_recording: {
      // Consumer social app: message bodies, phone numbers and payment fields
      // must never reach a replay. Masking inputs by default is the only safe
      // posture; individual non-sensitive fields can opt back in with
      // `data-ph-no-mask` / the `ph-no-capture` class where useful.
      maskAllInputs: true,
      maskTextSelector: "[data-ph-mask]",
      blockSelector: "[data-ph-block]",
      // JSON-LD is server-authored metadata, not replay content. Keeping it
      // out also avoids recorder failures on schema.org's `@context` key.
      captureJsonLd: false,
    },
    logs: {
      environment: deploymentEnvironment,
      captureConsoleLogs: false,
      serviceVersion: version,
      serviceName: `${name}-web`,
    },
    metrics: {
      serviceName: `${name}-web`,
      serviceVersion: version,
      environment: deploymentEnvironment,
      maxSeriesPerFlush: 500,
      resourceAttributes: telemetryContext,
    },
  });

  /**
   * Registered for every event, exception and replay in this document —
   * including events captured before the user is identified. Dashboards filter
   * preview traffic out with `deployment_environment`, and regressions are
   * attributed to a deploy with `release`.
   */
  observeAcquisitionUrl(window.location.href);
  posthog.register({
    ...telemetryContext,
    ...buildRuntimeContext(),
  });
}
